import { useEffect, useRef, useState } from 'react';

export const useNotificationsStream = ({
  user,
  BACKEND_URL,
  enableWebSockets = false,
  realtimeConnectedRef,
  showNotification,
  loadBarberData,
  loadBarberAcceptedQuickCuts,
  loadUpcomingBookings,
  loadClientAcceptedQuickCuts,
  loadClientHistory,
  setHasRequestNotification
}) => {
  const internalRealtimeRef = useRef(false);
  const effectiveRealtimeRef = realtimeConnectedRef || internalRealtimeRef;
  const sseRef = useRef(null);
  const sseConnectedRef = useRef(false);
  const wsRef = useRef(null);
  const wsConnectedRef = useRef(false);
  const [wsConnected, setWsConnected] = useState(false);
  const notificationRef = useRef({
    pendingTotal: 0,
    latestReviewId: null,
    latestBookingId: null,
    latestArrivedBookingId: null
  });

  useEffect(() => {
    if (!enableWebSockets) return;
    if (!user || !BACKEND_URL) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    const wsUrl = `${BACKEND_URL.replace(/^http/, 'ws')}/ws/notifications?token=${token}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      wsConnectedRef.current = true;
      setWsConnected(true);
      effectiveRealtimeRef.current = true;
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (!data || !data.type) return;

        if (user.user_type === 'barber') {
          loadBarberData?.();
          loadBarberAcceptedQuickCuts?.();
          if (data.type === 'quick_cut_update' && data.action === 'created') {
            showNotification?.('Nueva solicitud', 'Tienes nuevas solicitudes pendientes', 'info');
          }
          if (data.type === 'admin_message') {
            showNotification?.('Mensaje de administración', data.message || 'Tienes un nuevo mensaje', 'info');
          }
        } else if (user.user_type === 'client') {
          loadUpcomingBookings?.();
          loadClientAcceptedQuickCuts?.();
          loadClientHistory?.();
          if (data.type === 'booking_update') {
            showNotification?.('Reserva actualizada', 'Tu reserva se actualizó', 'info');
          }
          if (data.type === 'quick_cut_update' && data.action === 'accepted') {
            showNotification?.('Barbero encontrado', 'Tu solicitud fue aceptada', 'success');
          }
        }
      } catch (error) {
        // ignore
      }
    };

    ws.onerror = () => {
      wsConnectedRef.current = false;
      setWsConnected(false);
      if (!sseConnectedRef.current) effectiveRealtimeRef.current = false;
    };

    ws.onclose = () => {
      wsConnectedRef.current = false;
      setWsConnected(false);
      if (!sseConnectedRef.current) effectiveRealtimeRef.current = false;
    };

    return () => {
      if (wsRef.current === ws) {
        wsRef.current = null;
      }
      ws.close();
      wsConnectedRef.current = false;
      setWsConnected(false);
      if (!sseConnectedRef.current) effectiveRealtimeRef.current = false;
    };
  }, [
    user,
    BACKEND_URL,
    enableWebSockets,
    effectiveRealtimeRef,
    showNotification,
    loadBarberData,
    loadBarberAcceptedQuickCuts,
    loadUpcomingBookings,
    loadClientAcceptedQuickCuts,
    loadClientHistory
  ]);

  useEffect(() => {
    if (wsConnectedRef.current) return;
    if (!user || !BACKEND_URL) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }

    const stream = new EventSource(`${BACKEND_URL}/api/notifications/stream?token=${token}`);
    sseRef.current = stream;

    stream.onopen = () => {
      sseConnectedRef.current = true;
      if (!wsConnectedRef.current) effectiveRealtimeRef.current = true;
    };

    stream.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (!data || data.type === 'heartbeat') return;

        if (data.type === 'barber') {
          if (typeof data.pending_total === 'number') {
            if (data.pending_total > notificationRef.current.pendingTotal) {
              setHasRequestNotification?.(true);
              showNotification?.('Nueva solicitud', 'Tienes nuevas solicitudes pendientes', 'info');
            }
            notificationRef.current.pendingTotal = data.pending_total;
          }

          if (data.latest_review_id && data.latest_review_id !== notificationRef.current.latestReviewId) {
            notificationRef.current.latestReviewId = data.latest_review_id;
            loadBarberData?.();
            showNotification?.('Nueva reseña', 'Recibiste una nueva reseña', 'info');
          }

          if (data.latest_arrived_booking_id && data.latest_arrived_booking_id !== notificationRef.current.latestArrivedBookingId) {
            notificationRef.current.latestArrivedBookingId = data.latest_arrived_booking_id;
            loadBarberData?.();
            showNotification?.('Cliente llegó', 'Un cliente marcó su llegada', 'success');
          }
        }

        if (data.type === 'client') {
          if (data.latest_booking_id && data.latest_booking_id !== notificationRef.current.latestBookingId) {
            notificationRef.current.latestBookingId = data.latest_booking_id;
            loadClientHistory?.();
            showNotification?.('Reserva actualizada', 'Tu reserva se actualizó', 'info');
          }

          loadUpcomingBookings?.();
          loadClientAcceptedQuickCuts?.();
        }
      } catch (error) {
        // ignore
      }
    };

    stream.onerror = () => {
      sseConnectedRef.current = false;
      if (!wsConnectedRef.current) effectiveRealtimeRef.current = false;
    };

    return () => {
      stream.close();
      sseConnectedRef.current = false;
      if (!wsConnectedRef.current) effectiveRealtimeRef.current = false;
      if (sseRef.current === stream) {
        sseRef.current = null;
      }
    };
  }, [
    user,
    BACKEND_URL,
    wsConnected,
    effectiveRealtimeRef,
    showNotification,
    loadBarberData,
    loadUpcomingBookings,
    loadClientAcceptedQuickCuts,
    loadClientHistory,
    setHasRequestNotification
  ]);

  return { realtimeConnectedRef: effectiveRealtimeRef };
};
